<template>
  <div>
    <h2 class="mb-3">{{ $t('Edit template') }}</h2>
    <upgrade-ad v-if="isOnFreePlan"
                :message="$t('upgrade-ad.cb-stage3-edit')"
                :is-vertical="true"
                :classes="['upgrade-ad--cb-stage3-edit']"
    />
    <div class="py-3">
      <BigField class="py-3" :label="$t('Title')">
        <template slot="field">
          <input
            v-model="template.data.title"
            type="text"
            class="form-control form-input form-input-bordered w-full"
          >
        </template>
      </BigField>
    </div>

    <div class="py-3 border-t border-50">
      <ImageSelect :template="template.data.template" />
    </div>

    <div class="py-3 border-t border-50">
      <h4 class="py-3">{{ $t('Filters') }}</h4>

      <Field class="py-3" :label="$t('Entity item')">
        <template slot="field">
          <toggle-button
            :value="template.data.template.display_entity_item"
            :sync="true"
            :width="60"
            :height="26"
            :color="{
                      checked: 'var(--primary)',
                      unchecked: '#f2f2f2',
                      disabled: 'var(--20)'
                  }"
            @change="template.data.template.display_entity_item = arguments[0].value"
          />
        </template>
      </Field>

      <Field class="py-3" :label="$t('Total result & scores')">
        <template slot="field">
          <toggle-button
            :value="template.data.template.display_score"
            :sync="true"
            :width="60"
            :height="26"
            :color="{
                      checked: 'var(--primary)',
                      unchecked: '#f2f2f2',
                      disabled: 'var(--20)'
                  }"
            @change="updateScore(arguments[0].value)"
          />
        </template>
      </Field>

      <Field
        v-if="isRoccScoringEnabled()"
        class="py-3"
        :label="$t('template-builder.report-template.rocc-scoring')"
        :tooltip-text="$t('template-builder.report-template.rocc-scoring-tooltip')"
      >
        <template slot="field">
          <toggle-button
            :value="template.data.template.display_rocc_score"
            :sync="true"
            :width="60"
            :height="26"
            :color="{
                      checked: 'var(--primary)',
                      unchecked: '#f2f2f2',
                      disabled: 'var(--20)'
                  }"
            @change="updateRoccScore(arguments[0].value)"
          />
        </template>
      </Field>

      <Field class="py-3" :label="$t('Summary of negative answers')">
        <template slot="field">
          <toggle-button
            :value="template.data.template.display_negative_answers"
            :sync="true"
            :width="60"
            :height="26"
            :color="{
                      checked: 'var(--primary)',
                      unchecked: '#f2f2f2',
                      disabled: 'var(--20)'
                  }"
            @change="template.data.template.display_negative_answers = arguments[0].value"
          />
        </template>
      </Field>

      <Field class="py-3" :label="$t('Issues')">
        <template slot="field">
          <toggle-button
            :value="template.data.template.display_issues"
            :sync="true"
            :width="60"
            :height="26"
            :color="{
                      checked: 'var(--primary)',
                      unchecked: '#f2f2f2',
                      disabled: 'var(--20)'
                  }"
            @change="template.data.template.display_issues = arguments[0].value"
          />
        </template>
      </Field>

      <Field
        v-show="template.data.template.display_score"
        class="py-3"
        :label="$t('Result of pages')"
      >
        <template slot="field">
          <toggle-button
            :value="template.data.template.display_score_result_of_pages"
            sync
            :width="60"
            :height="26"
            :color="{
                      checked: 'var(--primary)',
                      unchecked: '#f2f2f2',
                      disabled: 'var(--20)'
                  }"
            @change="updateResultOfPages(arguments[0].value)"
          />
        </template>
      </Field>

      <Field class="py-3" :label="$t('Checklist description')">
        <template slot="field">
          <toggle-button
            :value="template.data.template.display_description"
            :sync="true"
            :width="60"
            :height="26"
            :color="{
                      checked: 'var(--primary)',
                      unchecked: '#f2f2f2',
                      disabled: 'var(--20)'
                  }"
            @change="template.data.template.display_description = arguments[0].value"
          />
        </template>
      </Field>

      <Field class="py-3" :label="$t('No answer required fields')">
        <template slot="field">
          <toggle-button
            :value="!template.data.template.exclude_questions_type_instruction"
            :sync="true"
            :width="60"
            :height="26"
            :color="{
                      checked: 'var(--primary)',
                      unchecked: '#f2f2f2',
                      disabled: 'var(--20)'
                  }"
            @change="template.data.template.exclude_questions_type_instruction = !arguments[0].value"
          />
        </template>
      </Field>

      <Field class="py-3" :label="$t('Unanswered check items')">
        <template slot="field">
          <toggle-button
            :value="!template.data.template.exclude_questions_not_responded"
            :sync="true"
            :width="60"
            :height="26"
            :color="{
                      checked: 'var(--primary)',
                      unchecked: '#f2f2f2',
                      disabled: 'var(--20)'
                  }"
            @change="template.data.template.exclude_questions_not_responded = !arguments[0].value"
          />
        </template>
      </Field>
    </div>

    <div
      v-if="template.data.template.display_score"
      class="py-3 border-t border-50"
    >
      <Field
        class="py-3"
        :label="$t('Display %-ranges for result')"
        :help-text="$t(
                        'E.g. If results is between 90% and 100%, results are titled as “Excellent”'
                    )"
      >
        <template slot="field">
          <toggle-button
            :value="template.data.template.display_score_range_labels"
            :sync="true"
            :width="60"
            :height="26"
            :color="{
                      checked: 'var(--primary)',
                      unchecked: '#f2f2f2',
                      disabled: 'var(--20)'
                  }"
            @change="updateDisplayScoreRange(arguments[0].value)"
          />
        </template>
      </Field>

      <template v-if="template.data.template.display_score_range_labels">
        <ul class="report-score-ranges text-80">
          <li
            v-for="(range, index) in template.data.template.score_range_labels"
            :key="index"
            class="mb-3"
          >
            <div class="flex items-center">
              <input
                v-model="range.min"
                type="text"
                class="form-control form-input form-input-bordered w-16"
                @change="handleRangeChange(index, range)"
              >
              <div class="flex-no-shrink whitespace-no-wrap px-2">
                % {{ $t('till') }}
              </div>
              <input
                v-model="range.max"
                type="text"
                class="form-control form-input form-input-bordered w-16"
                @change="handleRangeChange(index, range)"
              >
              <div class="flex-no-shrink whitespace-no-wrap px-2">
                % =
              </div>
              <input
                v-model="range.label"
                type="text"
                class="form-control form-input form-input-bordered"
                :placeholder="$t('Enter title')"
                style="width: 5.0rem;"
                @change="handleRangeChange(index, range)"
              >

              <select-control
                :multiple="false"
                :min-count="1"
                :html-labels="true"
                :options="colors"
                :value="range.color"
                direction="rtl"
                class="no-border w-16 mr-2"
                @input="range.color = arguments[0]"
              />

              <div class="w-16">
                <button
                  class="text-60 btn btn-small-icon"
                  @click.stop="deleteScoreRange(index)"
                >
                  <DeleteIcon />
                </button>
              </div>
            </div>

            <div
              v-if="rangeErrors && rangeErrors[index] !== null && rangeErrors.length > 0"
              class="text-red">
              <ul>
                <li v-for="error in rangeErrors[index]">{{ error }}</li>
              </ul>
            </div>

          </li>
        </ul>
        <button
          class="btn btn-default btn-secondary mb-3"
          @click.stop="addScoreRange"
        >
          {{ $t('Add range') }}
        </button>
      </template>
    </div>

    <div
      v-if="isWeightedScoreCalculationEnabled()"
      class="py-3 border-t border-50"
    >

      <Field
        class="py-3"
        :label="$t('template-builder.report-template.average-weighted-score')"
      >
        <template slot="after-label">
          <Tooltip
            :label="$t('template-builder.report-template.average-weighted-score-explanation')"
            :multilined="true"
          >
            <icon
              size="xs"
              icon-pack="fal"
              icon="info-circle"
              classes="feature-explanation-icon"
            />
          </Tooltip>

        </template>
        <template slot="field">
          <toggle-button
            :value="template.data.template.average_weighted_score_calculation"
            :sync="true"
            :width="60"
            :height="26"
            :color="{
                      checked: 'var(--primary)',
                      unchecked: '#f2f2f2',
                      disabled: 'var(--20)'
                  }"
            @change="updateAverageWeightedScoreCalculation(arguments[0].value)"
          />
        </template>
      </Field>
    </div>

    <div class="py-3 border-t border-50">
      <Field
        class="py-3"
        :label="$t('Define report template as default')"
      >
        <template slot="field">
          <toggle-button
            :value="!!template.data.isDefault"
            :sync="true"
            :width="60"
            :height="26"
            :color="{
                      checked: 'var(--primary)',
                      unchecked: '#f2f2f2',
                      disabled: 'var(--20)'
                  }"
            @change="updateIsDefault(arguments[0].value)"
          />
        </template>
      </Field>
    </div>

    <template v-if="!isOnFreePlan">
      <starred-ad-item :is-starred="!hasFeature('customReports')">
        <button class="btn btn-default btn-primary mr-3"
                @click="handleSave"
        >
          {{ $t('Save') }}
        </button>
      </starred-ad-item>

      <button class="btn btn-default btn-secondary" @click="$emit('discard')">
        {{ $t('Discard changes') }}
      </button>
    </template>
    <upgrade-ad v-else
                with-dropdown
                :message="$t('upgrade-ad.cb-stage3-edit')"
                is-vertical
                :classes="['upgrade-ad--cb-stage3-edit-cta']"
    >
      <div slot="fields">
        <starred-ad-item :is-starred="!hasFeature('customReports')">
          <button class="btn btn-default btn-primary mr-3"
                  disabled
                  @click="handleSave"
          >
            {{ $t('Save') }}
          </button>
        </starred-ad-item>

        <button class="btn btn-default btn-secondary" @click="$emit('discard')">
          {{ $t('Discard changes') }}
        </button>
      </div>
    </upgrade-ad>
  </div>
</template>

<script>
  import BigField from '@/_legacy/views/ChecklistBuilder/BigField';
  import Field from '@/_legacy/views/ChecklistBuilder/Field';
  import ImageSelect from '@/_legacy/views/ChecklistBuilder/Report/ImageSelect';
  import DeleteIcon from '@/_legacy/views/ChecklistBuilder/Icons/DeleteIcon';
  import authMixin from '@/_legacy/mixins/authMixin';
  import { eventBus } from '@/_legacy/eventBus';
  import { UpgradeAd } from '@/ui/components/upgrade/UpgradeAd';
  import { StarredAdItem } from '@/ui/components/upgrade/StarredAdItem';
  import { Events } from '@/ui/events';
  import { isRoccScoringEnabled, isWeightedScoreCalculationEnabled } from '@/ui/helpers/featureHelper';
  import { Tooltip } from '@/ui/components/Tooltip';
  import { Icon } from '@/ui/components/icon/Icon';

  export default {
    components: {
      Icon,
      StarredAdItem,
      UpgradeAd,
      BigField,
      Field,
      ImageSelect,
      DeleteIcon,
      Tooltip,
    },

    mixins: [
      authMixin,
    ],

    props: {
      template: {
        type: Object,
        required: true,
      },
      resourceId: {
        type: String,
        default: null,
      },
    },

    data: () => ({
      rangeErrors: [],
    }),

    computed: {
      colors() {
        return [
          {
            value: '#4AD862',
            label:
              '<span class="rounded-lg" style="width:20px; height:20px; display:inline-block; background: #4AD862"></span>',
          },
          {
            value: '#FDAC43',
            label:
              '<span class="rounded-lg" style="width:20px; height:20px; display:inline-block; background: #FDAC43"></span>',
          },
          {
            value: '#FF6E44',
            label:
              '<span class="rounded-lg" style="width:20px; height:20px; display:inline-block; background: #FF6E44"></span>',
          },
          {
            value: '#777777',
            label:
              '<span class="rounded-lg" style="width:20px; height:20px; display:inline-block; background: #777777"></span>',
          },
          {
            value: '#1fb6ff',
            label:
              '<span class="rounded-lg" style="width:20px; height:20px; display:inline-block; background: #1fb6ff"></span>',
          },
          {
            value: '#BA31ED',
            label:
              '<span class="rounded-lg" style="width:20px; height:20px; display:inline-block; background: #BA31ED"></span>',
          },
          {
            value: '#1BE7FF',
            label:
              '<span class="rounded-lg" style="width:20px; height:20px; display:inline-block; background: #1BE7FF"></span>',
          },
          {
            value: '#FF49DB',
            label:
              '<span class="rounded-lg" style="width:20px; height:20px; display:inline-block; background: #FF49DB"></span>',
          },
          {
            value: '#F5D400',
            label:
              '<span class="rounded-lg" style="width:20px; height:20px; display:inline-block; background: #F5D400"></span>',
          },
          {
            value: '#60D956',
            label:
              '<span class="rounded-lg" style="width:20px; height:20px; display:inline-block; background: #60D956"></span>',
          },
          {
            value: '#7C69F8',
            label:
              '<span class="rounded-lg" style="width:20px; height:20px; display:inline-block; background: #7C69F8"></span>',
          },
          {
            value: '#60D2C9',
            label:
              '<span class="rounded-lg" style="width:20px; height:20px; display:inline-block; background: #60D2C9"></span>',
          },
        ];
      },
      formValidates() {
        return this.rangeErrors.find(error => (error !== null && error.length > 0)) === undefined;
      },
    },

    watch: {
      template: {
        immediate: true,
        handler() {
          this.validateRanges(true);
        },
      },
    },

    mounted() {
      eventBus.$on(Events.saveReportTemplate, resolve => {
        const response = this.handleSave(resolve);

        if (!resolve) {
          resolve(response);
        }
      });
    },

    methods: {
      isRoccScoringEnabled,
      isWeightedScoreCalculationEnabled,
      validateRange(range, saving = false) {
        const errors = [];
        if (
          saving && (
            !(parseFloat(range.min) >= 0)
            || !(parseFloat(range.max) >= 0)
            || range.label === null
            || range.label.trim().length === 0
          )
        ) {
          errors.push(this.$t('Please specify all values'));
        }
        if (parseFloat(range.min) >= parseFloat(range.max)) {
          errors.push(this.$t('The 1. value must be smaller than the 2. value'));
        }
        if (errors.length > 0) {
          return errors;
        }
        return null;
      },

      handleRangeChange(index, range) {
        this.$set(this.rangeErrors, index, this.validateRange(range));
      },

      updateScore(value) {
        this.template.data.template.display_score = value;
        this.updateDisplayScoreRange(false);
        this.updateResultOfPages(false);
      },

      updateRoccScore(value) {
        this.template.data.template.display_rocc_score = value;
      },

      updateChecklistId(value) {
        this.template.data.checklist = value ? null : this.resourceId;
      },
      updateIsDefault(value) {
        this.template.data.isDefault = value ? 1 : 0;
        if (this.template.data.isDefault) {
          this.template.checklist = null;
        }
      },

      updateDisplayScoreRange(value) {
        this.template.data.template.display_score_range_labels = value;
        if (this.template.data.template.display_score_range_labels) {
          this.addScoreRange();
        } else {
          this.template.data.template.score_range_labels = [];
        }
      },

      updateAverageWeightedScoreCalculation(value) {
        this.template.data.template.average_weighted_score_calculation = value;
        this.$store.commit('checklist_builder/updateAverageWeightedScoreCalculation', value);
      },

      updateResultOfPages(value) {
        this.$set(this.template.data.template, 'display_score_result_of_pages', value);
      },

      deleteScoreRange(index) {
        this.template.data.template.score_range_labels.splice(index, 1);
      },
      addScoreRange() {
        this.template.data.template.score_range_labels.push({
          min: null,
          max: null,
          label: '',
          color: '#4AD862',
        });
      },

      validateRanges(saving = false) {
        this.rangeErrors = this.template.data.template.score_range_labels.map(range => this.validateRange(range, saving));
      },

      handleSave(resolveCallback) {
        if (!this.hasFeature('customReports')) {
          eventBus.$emit('free-plan-limit', {
            message: {
              text: this.$t('This feature is not included in your free plan.'),
              advice: this.$t('Upgrade now to customize reports however you want.'),
            },
          });

          return false;
        }

        this.validateRanges(true);
        if (this.formValidates) {
          this.$emit('save', resolveCallback);
          return true;
        }
        eventBus.$emit('error', 'The template has errors, please fix them first.');
        return false;
      },
    },
  };
</script>
